import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import config from "../../Config";
import * as Sentry from "@sentry/react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Stack,
    Alert,
    useTheme,
    useMediaQuery,
    CircularProgress
} from "@mui/material";
import styles from "./Login.styles";
import SnackbarNotification from "../../components/SnackbarNotification";

const Login = () => {
    // Form state
    const [login, setLogin] = useState("");
    const [password, setPassword] = useState("");
    const [loginError, setLoginError] = useState("");
    const [passwordError, setPasswordError] = useState("");

    // UI state
    const [loading, setLoading] = useState(false);
    const [verificationRequired, setVerificationRequired] = useState(false);
    const [userId, setUserId] = useState(null);
    const [userEmail, setUserEmail] = useState("");
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "error"
    });

    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const validateForm = () => {
        let isValid = true;
        setLoginError("");
        setPasswordError("");

        if (login.trim() === "") {
            setLoginError("Please enter your login or email");
            isValid = false;
        }

        if (password.trim() === "") {
            setPasswordError("Please enter a password");
            isValid = false;
        } else if (password.length < 8) {
            setPasswordError("The password must be 8 characters or longer");
            isValid = false;
        }

        return isValid;
    };

    const onButtonClick = () => {
        if (loading) return;
        if (!validateForm()) return;

        setLoading(true);
        logIn().finally(() => setLoading(false));
    };

    const findUserByLogin = async () => {
        try {
            const response = await fetch(`${config.apiBaseUrl}/user/find-by-login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ login })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to find user');
            }

            const data = await response.json();
            if (!data.id) {
                throw new Error('User ID not found');
            }

            return data.id;
        } catch (error) {
            console.error('Error finding user:', error);
            setSnackbar({
                open: true,
                message: 'Failed to identify user. Please check your login/email.',
                severity: 'error'
            });
            return null;
        }
    };

    const handleResendVerification = async () => {
        setLoading(true);

        try {
            // If we don't have user ID, get it first
            const id = userId || await findUserByLogin();
            if (!id) {
                setLoading(false);
                return;
            }

            // Request verification email
            const response = await fetch(`${config.apiBaseUrl}/user/resend-verification/${id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (response.ok) {
                setSnackbar({
                    open: true,
                    message: 'Verification email sent! Please check your inbox.',
                    severity: 'success'
                });
            } else {
                setSnackbar({
                    open: true,
                    message: data.detail || 'Failed to send verification email',
                    severity: 'error'
                });
            }
        } catch (error) {
            console.error('Error resending verification:', error);
            setSnackbar({
                open: true,
                message: 'An error occurred while sending the verification email',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyEmail = () => {
        navigate("/verify-email", {
            state: {
                fromLogin: true,
                email: userEmail || login
            }
        });
    };

    const logIn = async () => {
        try {
            console.log("Attempting login with:", login);

            const response = await fetch(`${config.apiBaseUrl}/user/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ login, password }),
            });

            // Parse response
            const data = await response.json();
            console.log("Login response:", response.status, data);

            if (response.ok && data.token) {
                // Store user data in localStorage
                const userData = {
                    login,
                    token: data.token,
                    id: data.user.id,
                    user_info: data.user
                };

                console.log("Storing user data:", userData);
                localStorage.setItem("user", JSON.stringify(userData));

                // Use window.location for a full page reload to ensure state is refreshed
                window.location.href = "/";
                return;
            }

            // Handle error cases
            if (response.status === 403 && data.detail?.includes("Email not verified")) {
                // Email verification required
                setVerificationRequired(true);
                setUserEmail(login);

                // Try to find user ID
                const id = await findUserByLogin();
                if (id) setUserId(id);

                setSnackbar({
                    open: true,
                    message: "Please verify your email before logging in",
                    severity: "warning"
                });
            } else if (response.status === 401) {
                // Invalid credentials
                setSnackbar({
                    open: true,
                    message: "Invalid username or password",
                    severity: "error"
                });
            } else {
                // Other errors
                Sentry.captureMessage("Login error", {
                    level: "error",
                    extra: { response: data },
                });
                setSnackbar({
                    open: true,
                    message: data.detail || "An error occurred during login",
                    severity: "error"
                });
            }
        } catch (error) {
            console.error("Login error:", error);
            Sentry.captureException(error);
            setSnackbar({
                open: true,
                message: "An error occurred. Please try again.",
                severity: "error"
            });
        }
    };

    return (
        <Box sx={styles.container(theme, isMobile)}>
            {/* Left panel with branding (hidden on mobile) */}
            {!isMobile && (
                <Box sx={styles.leftPanel}>
                    <Typography variant="h3" sx={styles.title(theme)}>
                        horizon data wave
                    </Typography>
                    <Box sx={styles.separator(theme)} />
                    <Typography variant="subtitle1" sx={styles.subtitle(theme)}>
                        The First{" "}
                        <Box component="span" sx={{ color: theme.palette.primary.main, fontWeight: "bold" }}>
                            AI-Ready
                        </Box>{" "}
                        Data Provider
                    </Typography>
                    <Typography variant="body2" sx={styles.description(theme)}>
                        Data from Social Media and Open Sources about people and companies
                        for your AI-driven Project
                    </Typography>
                </Box>
            )}

            {/* Main form area */}
            <Box sx={styles.formContainer}>
                <Box sx={styles.formBox}>
                    {verificationRequired ? (
                        // Verification required UI
                        <>
                            <Typography variant="h6" gutterBottom sx={styles.formTitle}>
                                Email Verification Required
                            </Typography>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Your account requires email verification.
                                Please check your inbox for a verification email.
                            </Alert>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={handleResendVerification}
                                sx={{ mb: 2 }}
                                disabled={loading}
                                startIcon={loading && <CircularProgress size={20} />}
                            >
                                {loading ? "Sending..." : "Resend Verification Email"}
                            </Button>
                            <Button
                                variant="outlined"
                                color="primary"
                                fullWidth
                                onClick={handleVerifyEmail}
                                sx={{ mb: 2 }}
                            >
                                Go to Verification Page
                            </Button>
                            <Button
                                variant="text"
                                color="primary"
                                fullWidth
                                onClick={() => setVerificationRequired(false)}
                            >
                                Back to Login
                            </Button>
                        </>
                    ) : (
                        // Login form UI
                        <>
                            <Typography variant="h6" gutterBottom sx={styles.formTitle}>
                                welcome to horizondatawave.ai
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={styles.formSubtitle}>
                                Please log in to your account
                            </Typography>
                            <Stack spacing={2}>
                                <TextField
                                    label="Login or Email"
                                    variant="outlined"
                                    fullWidth
                                    value={login}
                                    onChange={(e) => setLogin(e.target.value)}
                                    error={!!loginError}
                                    helperText={loginError}
                                    inputProps={{
                                        autoComplete: "username email"
                                    }}
                                    disabled={loading}
                                />
                                <TextField
                                    label="Password"
                                    variant="outlined"
                                    type="password"
                                    fullWidth
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    error={!!passwordError}
                                    helperText={passwordError}
                                    inputProps={{
                                        autoComplete: "current-password"
                                    }}
                                    disabled={loading}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    sx={styles.button}
                                    onClick={onButtonClick}
                                    disabled={loading}
                                    startIcon={loading && <CircularProgress size={20} />}
                                >
                                    {loading ? "Logging in..." : "LOG IN"}
                                </Button>
                            </Stack>
                            <Typography variant="body2" color="textSecondary" sx={styles.linkText}>
                                Don't have an account? <Link to="/register">Sign Up</Link>
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1, textAlign: 'right' }}>
                                <Link to="/forgot-password">Forgot password?</Link>
                            </Typography>
                        </>
                    )}
                </Box>
            </Box>

            {/* Notification snackbar */}
            <SnackbarNotification
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            />
        </Box>
    );
};

export default Login;